import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faFileDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageTitle from '../../../layouts/PageTitle';
import Select from 'react-select';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { NoData } from '../../SharedComponents/NoData';
import { Link, useHistory } from 'react-router-dom';
import VerificationDetailsModal from '../../Accounting/Verification/VerificationDetailsModal';

const ResultReport = () => {
    const [fiscalYears, setFiscalYears] = useState([]);
    const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [processedData, setProcessedData] = useState(null);

    const [expandedSections, setExpandedSections] = useState({});
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [yearPeriodOptions, setYearPeriodOptions] = useState([]);
    const [showAllYears, setShowAllYears] = useState(false);
    const [showVerifications, setShowVerifications] = useState(false);
    const [expandedAccounts, setExpandedAccounts] = useState({});
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [selectedVerification, setSelectedVerification] = useState(null);

    const { companyId } = useParams();
    const history = useHistory();

    // Define the standard Swedish income statement structure
    const getStandardIncomeStatementStructure = () => {
        return {
            'Rörelseintäkter, lagerförändringar m.m.': {
                'Nettoomsättning': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['30', '31'] // Account ranges that belong here
                },
                'Aktiverat arbete för egen räkning': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['38']
                },
                'Övriga rörelseintäkter': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['37', '39'] // Including 37xx for misc income
                },
                total_change: 0
            },
            'Rörelsekostnader': {
                'Råvaror och förnödenheter': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['40', '41', '42', '43', '44', '45']
                },
                'Övriga externa kostnader': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '60', '61', '62', '63', '64', '65', '66', '67', '68', '69']
                },
                'Personalkostnader': {
                    'Löner och arvoden': {
                        accounts: [],
                        total_change: 0,
                        accountRanges: ['70', '71', '72']
                    },
                    'Sociala avgifter': {
                        accounts: [],
                        total_change: 0,
                        accountRanges: ['73', '74', '75']
                    },
                    'Pensionskostnader': {
                        accounts: [],
                        total_change: 0,
                        accountRanges: ['76']
                    },
                    total_change: 0
                },
                'Avskrivningar av materiella och immateriella anläggningstillgångar': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['77', '78']
                },
                'Nedskrivningar av materiella och immateriella anläggningstillgångar': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['774', '779']
                },
                'Övriga rörelsekostnader': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['79']
                },
                total_change: 0
            },
            'Finansiella poster': {
                'Resultat från andelar i koncernföretag': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['80']
                },
                'Resultat från andelar i intresseföretag': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['81']
                },
                'Resultat från övriga värdepapper och fordringar som är anläggningstillgångar': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['82']
                },
                'Övriga ränteintäkter och liknande resultatposter': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['83']
                },
                'Räntekostnader och liknande resultatposter': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['84']
                },
                total_change: 0
            },
            'Bokslutsdispositioner': {
                'Förändring av periodiseringsfonder': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['881']
                },
                'Förändring av överavskrivningar': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['885']
                },
                'Övriga bokslutsdispositioner': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['886', '889']
                },
                total_change: 0
            },
            'Skatter': {
                'Skatt på årets resultat': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['891', '892', '893']
                },
                'Övriga skatter': {
                    accounts: [],
                    total_change: 0,
                    accountRanges: ['898']
                },
                total_change: 0
            }
        };
    };

    useEffect(() => {
        getFiscalYears();
    }, [companyId]);

    useEffect(() => {
        if (selectedFiscalYear && selectedPeriod) {
            getResultReport();
        }
    }, [selectedFiscalYear, selectedPeriod, showVerifications]);

    // Initialize all sections as expanded
    useEffect(() => {
        const initialExpanded = {};
        const sections = [
            'Rörelseintäkter, lagerförändringar m.m.',
            'Rörelsekostnader',
            'Finansiella poster',
            'Bokslutsdispositioner',
            'Skatter'
        ];
        sections.forEach(section => {
            initialExpanded[section] = true;
        });
        setExpandedSections(initialExpanded);
    }, []);

    const getFiscalYears = async () => {
        try {
            const result = await axios.get(getApiUrl(`${companyId}/accounting/fiscalyears`), { withCredentials: true });
            setFiscalYears(result.data.fiscalYears);
            if (result.data.fiscalYears.length > 0) {
                const latestFiscalYear = result.data.fiscalYears.sort((a, b) => b.year - a.year)[0];
                setSelectedFiscalYear(latestFiscalYear);
            }
        } catch (err) {
            setError('Failed to fetch fiscal years');
            setFiscalYears([]);
        } finally {
            setLoading(false);
        }
    };

    // Build grouped Year/Period options (whole year + months) like BalanceReport
    useEffect(() => {
        if (!fiscalYears || fiscalYears.length === 0) return;

        const monthFormatter = new Intl.DateTimeFormat('sv-SE', { month: 'long' });
        const toLocalYMD = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        const sorted = [...fiscalYears].sort((a, b) => b.year - a.year);
        const limited = showAllYears ? sorted : sorted.slice(0, 5);

        const groups = limited.map((fy) => {
            const fyStart = new Date(fy.start_date);
            const fyEnd = new Date(fy.end_date);

            const opts = [];
            opts.push({
                value: { fiscalYear: fy, period: { type: 'year', startDate: fy.start_date, endDate: fy.end_date } },
                label: 'Hela året'
            });

            let cursor = new Date(fyStart.getFullYear(), fyStart.getMonth(), 1);
            while (cursor <= fyEnd) {
                const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
                const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
                const start = monthStart < fyStart ? fyStart : monthStart;
                const end = monthEnd > fyEnd ? fyEnd : monthEnd;
                const monthName = monthFormatter.format(monthStart);
                const label = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${monthStart.getFullYear()}`;

                opts.push({
                    value: { fiscalYear: fy, period: { type: 'month', startDate: toLocalYMD(start), endDate: toLocalYMD(end) } },
                    label
                });

                cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
            }

            return { label: String(fy.year), options: opts };
        });

        const hasMore = !showAllYears && sorted.length > limited.length;
        const moreGroup = hasMore ? [{ label: '', options: [{ value: { action: '__more__' }, label: 'Visa fler år…' }] }] : [];
        setYearPeriodOptions([...groups, ...moreGroup]);

        // Default selection (latest FY whole year)
        const latestGroup = groups[0];
        if (latestGroup && latestGroup.options && latestGroup.options.length > 0) {
            const def = latestGroup.options[0];
            setSelectedFiscalYear(def.value.fiscalYear);
            setSelectedPeriod({ value: def.value.period, label: def.label });
        }
    }, [fiscalYears, showAllYears]);

    const getResultReport = async () => {
        if (!selectedFiscalYear || !selectedPeriod) return;

        setLoading(true);
        try {
            const isWholeYear = selectedPeriod.value?.type === 'year';
            const params = {
                fiscalYearId: selectedFiscalYear.fiscal_year_id,
                includeComparison: isWholeYear ? 'true' : 'false',
                includeVerifications: showVerifications ? 'true' : 'false'
            };
            if (!isWholeYear) {
                params.startDate = selectedPeriod.value.startDate;
                params.endDate = selectedPeriod.value.endDate;
            }

            const response = await axios.get(getApiUrl(`${companyId}/reporting/result`), {
                params,
                withCredentials: true
            });

            let dataMap;
            if (isWholeYear) {
                dataMap = response.data.data; // multi-year comparison
            } else {
                // single period; wrap into comparison-like shape
                dataMap = {
                    [selectedFiscalYear.year]: {
                        fiscalYear: selectedFiscalYear,
                        data: response.data
                    }
                };
            }

            setReportData(dataMap);
            const processed = processMultiYearData(dataMap);
            setProcessedData(processed);
            setError(null);
        } catch (err) {
            setError('Failed to fetch result report');
            setReportData(null);
            setProcessedData(null);
        } finally {
            setLoading(false);
        }
    };

    // Function to determine which section an account belongs to
    const findAccountSection = (accountNumber, structure) => {
        const accountPrefix2 = accountNumber.substring(0, 2);
        const accountPrefix3 = accountNumber.substring(0, 3);

        const findInStructure = (obj, path = []) => {
            for (const [key, value] of Object.entries(obj)) {
                if (value && typeof value === 'object') {
                    if (value.accountRanges) {
                        // Check if this account belongs to this section
                        const matches = value.accountRanges.some(range =>
                            accountNumber.startsWith(range) ||
                            accountPrefix2 === range ||
                            accountPrefix3 === range
                        );
                        if (matches) {
                            return [...path, key];
                        }
                    } else {
                        // Recurse into subsections
                        const result = findInStructure(value, [...path, key]);
                        if (result) return result;
                    }
                }
            }
            return null;
        };

        return findInStructure(structure);
    };

    // Collect all unique accounts from all years
    const collectAllAccounts = (data) => {
        const allAccounts = new Map();

        Object.values(data).forEach(yearData => {
            if (yearData && yearData.data) {
                collectAccountsRecursively(yearData.data, allAccounts);
            }
        });

        return allAccounts;
    };

    const collectAccountsRecursively = (obj, accountsMap) => {
        if (!obj || typeof obj !== 'object') return;

        Object.values(obj).forEach(value => {
            if (value && typeof value === 'object') {
                if (value.accounts && Array.isArray(value.accounts)) {
                    value.accounts.forEach(account => {
                        accountsMap.set(account.account_id, {
                            account_id: account.account_id,
                            account_number: account.account_number,
                            account_name: account.account_name
                        });
                    });
                } else {
                    collectAccountsRecursively(value, accountsMap);
                }
            }
        });
    };

    // Main processing function
    const processMultiYearData = (data) => {
        if (!data || !Object.keys(data).length) return null;

        const allYearsSortedAsc = Object.keys(data).map(y => parseInt(y, 10)).sort((a,b) => a-b);
        // Limit to last 3 years, show most recent first
        const availableYears = allYearsSortedAsc.slice(-3).reverse().map(String);

        // Step 1: Get the standard structure
        const standardStructure = getStandardIncomeStatementStructure();

        // Step 2: Collect all accounts from all years
        const allAccounts = collectAllAccounts(data);

        // Step 3: Assign accounts to their correct sections in the structure
        const structureWithAccounts = assignAccountsToStructure(standardStructure, allAccounts);

        // Step 4: Create data for each year using the unified structure
        const processedYearData = {};
        availableYears.forEach(year => {
            processedYearData[year] = createYearData(structureWithAccounts, data[year], allAccounts);
        });

        return {
            years: availableYears,
            data: processedYearData,
            originalData: data,
            allAccounts: allAccounts,
            structure: structureWithAccounts
        };
    };

    // Assign all accounts to their correct sections
    const assignAccountsToStructure = (structure, allAccounts) => {
        const newStructure = JSON.parse(JSON.stringify(structure)); // Deep copy

        allAccounts.forEach((account) => {
            const sectionPath = findAccountSection(account.account_number, structure);
            if (sectionPath) {
                // Navigate to the correct section and add the account
                let currentSection = newStructure;
                for (const pathPart of sectionPath) {
                    currentSection = currentSection[pathPart];
                }

                if (currentSection && currentSection.accounts) {
                    currentSection.accounts.push({
                        account_id: account.account_id,
                        account_number: account.account_number,
                        account_name: account.account_name,
                        opening_balance: "0.00",
                        change: "0.00",
                        closing_balance: "0.00"
                    });
                }
            } else {
                console.warn(`Could not find section for account ${account.account_number} - ${account.account_name}`);
            }
        });

        // Sort accounts in each section by account number
        sortAccountsInStructure(newStructure);

        return newStructure;
    };

    // Sort accounts by account number in all sections
    const sortAccountsInStructure = (structure) => {
        Object.values(structure).forEach(value => {
            if (value && typeof value === 'object') {
                if (value.accounts && Array.isArray(value.accounts)) {
                    value.accounts.sort((a, b) =>
                        parseInt(a.account_number) - parseInt(b.account_number)
                    );
                } else {
                    sortAccountsInStructure(value);
                }
            }
        });
    };

    // Create year-specific data by populating the structure with actual values
    const createYearData = (structure, yearData, allAccounts) => {
        const yearStructure = JSON.parse(JSON.stringify(structure)); // Deep copy

        if (!yearData || !yearData.data) {
            // No data for this year, return structure with all zeros
            return yearStructure;
        }

        // Populate with actual values from this year's data
        populateStructureWithYearData(yearStructure, yearData.data, allAccounts);

        // Recalculate all totals
        recalculateAllTotals(yearStructure);

        return yearStructure;
    };

    // Populate structure with actual data from a specific year
    const populateStructureWithYearData = (structure, yearData, allAccounts) => {
        // Create a map of account values from the year data
        const accountValues = new Map();
        collectAccountValuesRecursively(yearData, accountValues);
        // Update the structure with actual values
        updateStructureWithValues(structure, accountValues);
    };

    const collectAccountValuesRecursively = (obj, valuesMap) => {
        if (!obj || typeof obj !== 'object') return;

        Object.values(obj).forEach(value => {
            if (value && typeof value === 'object') {
                if (value.accounts && Array.isArray(value.accounts)) {
                    value.accounts.forEach(account => {
                        const accountData = {
                            opening_balance: account.opening_balance || "0.00",
                            change: account.change || "0.00",
                            closing_balance: account.closing_balance || "0.00",
                            account_number: account.account_number // Store account number for fallback matching
                        };

                        // Include verification data if it exists
                        if (account.verifications) {
                            accountData.verifications = account.verifications;
                        }

                        // Store under multiple keys for robust lookup
                        valuesMap.set(account.account_id, accountData);
                        valuesMap.set(String(account.account_id), accountData);
                        valuesMap.set(Number(account.account_id), accountData);
                        if (account.account_number) {
                            valuesMap.set(account.account_number, accountData);
                        }
                    });
                } else {
                    collectAccountValuesRecursively(value, valuesMap);
                }
            }
        });
    };

    const updateStructureWithValues = (structure, valuesMap) => {
        Object.values(structure).forEach(value => {
            if (value && typeof value === 'object') {
                if (value.accounts && Array.isArray(value.accounts)) {
                    value.accounts.forEach(account => {
                        // Try direct ID match first
                        let accountValue = valuesMap.get(account.account_id);
                        
                        // If not found, try converting ID to string/number for match
                        if (!accountValue) {
                            accountValue = valuesMap.get(String(account.account_id)) || valuesMap.get(Number(account.account_id));
                        }
                        
                        // If still not found, try account number match
                        if (!accountValue) {
                            for (const [key, value] of valuesMap.entries()) {
                                // Check if we can find by account number in the original data
                                if (value.account_number && value.account_number === account.account_number) {
                                    accountValue = value;
                                    break;
                                }
                            }
                        }
                        
                        if (accountValue) {
                            account.opening_balance = accountValue.opening_balance;
                            account.change = accountValue.change;
                            account.closing_balance = accountValue.closing_balance;
                            // Copy verification data if it exists
                            if (accountValue.verifications) {
                                account.verifications = accountValue.verifications;
                            }
                            
                        } 
                    });
                } else {
                    updateStructureWithValues(value, valuesMap);
                }
            }
        });
    };

    // Recalculate totals for the entire structure
    const recalculateAllTotals = (structure) => {
        Object.entries(structure).forEach(([key, value]) => {
            if (value && typeof value === 'object' && key !== 'accounts') {
                if (value.accounts && Array.isArray(value.accounts)) {
                    // This is a leaf section with accounts - calculate its total
                    value.total_change = value.accounts.reduce((sum, account) => {
                        const changeVal = String(account.change || 0).replace(/,/g, '');
                        const parsedChange = parseFloat(changeVal);
                        return sum + (isNaN(parsedChange) ? 0 : parsedChange);
                    }, 0);
                } else {
                    // This is a parent section - recurse and sum children
                    recalculateAllTotals(value);
                    value.total_change = Object.entries(value)
                        .filter(([k, v]) => k !== 'total_change' && k !== 'accountRanges' && v && typeof v === 'object')
                        .reduce((sum, [k, v]) => sum + (parseFloat(v.total_change) || 0), 0);
                }
            }
        });
    };

    const handleYearPeriodChange = (opt) => {
        if (!opt || !opt.value) return;
        if (opt.value.action === '__more__') {
            setShowAllYears(true);
            return;
        }
        setSelectedFiscalYear(opt.value.fiscalYear);
        setSelectedPeriod({ value: opt.value.period, label: opt.label });
    };

    const toggleSection = (sectionPath) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionPath]: !prev[sectionPath]
        }));
    };

    const toggleAccount = (accountId) => {
        setExpandedAccounts(prev => ({
            ...prev,
            [accountId]: !prev[accountId]
        }));
    };

    const handleVerificationClick = async (verification) => {
        try {
            // Fetch full verification details
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/verifications/${verification.verification_id}`),
                { withCredentials: true }
            );
            
            setSelectedVerification(response.data);
            setShowVerificationModal(true);
        } catch (error) {
            // Still show modal with limited data if full fetch fails
            setSelectedVerification({
                verification_id: verification.verification_id,
                series: verification.verification_number?.charAt(0) || 'A',
                number: verification.verification_number?.slice(1) || verification.verification_number,
                date: verification.date,
                description: verification.description,
                transactions: []
            });
            setShowVerificationModal(true);
        }
    };

    const formatAmount = (amount) => {
        if (amount === undefined || amount === null || amount === '') return '';
        const numAmount = parseFloat(String(amount).replace(/,/g, ''));
        if (isNaN(numAmount)) {
            return '';
        }
        const formatted = new Intl.NumberFormat('sv-SE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            useGrouping: true
        }).format(Math.abs(numAmount));

        return numAmount < 0 ? `(${formatted})` : formatted;
    };

    const formatAmountShowZero = (amount) => {
        if (amount === undefined || amount === null) return '0';
        const numAmount = parseFloat(amount) || 0;
        return new Intl.NumberFormat('sv-SE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            useGrouping: true,
            signDisplay: 'exceptZero'
        }).format(numAmount);
    };

    const formatAmountWithSign = (amount) => {
        if (!amount || amount === 0) return '0';
        const numAmount = parseFloat(amount);
        return new Intl.NumberFormat('sv-SE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            useGrouping: true,
            signDisplay: 'exceptZero'
        }).format(numAmount);
    };

    const getSectionTitle = (sectionName) => {
        const titles = {
            'Rörelseintäkter, lagerförändringar m.m.': 'Rörelsens intäkter',
            'Rörelsekostnader': 'Rörelsens kostnader',
            'Finansiella poster': 'Finansiella poster',
            'Bokslutsdispositioner': 'Bokslutsdispositioner',
            'Skatter': 'Skatter'
        };
        return titles[sectionName] || sectionName;
    };

    // Helper to get section by path
    const getSectionByPath = (data, path) => {
        // Handle special case where the path is just a section name with dots
        if (data && data[path]) {
            return data[path];
        }

        // Smart path splitting: handle section names that contain dots
        const smartSplitPath = (path) => {
            // List of known section names that contain dots
            const sectionsWithDots = [
                'Rörelseintäkter, lagerförändringar m.m.'
            ];
            
            for (const sectionName of sectionsWithDots) {
                if (path.startsWith(sectionName + '.')) {
                    // Split on the first dot AFTER the section name
                    const remainder = path.substring(sectionName.length + 1);
                    return [sectionName, ...remainder.split('.')];
                } else if (path === sectionName) {
                    return [sectionName];
                }
            }
            
            // Default: split on all dots
            return path.split('.');
        };

        const pathParts = smartSplitPath(path);
        let current = data;

        for (const part of pathParts) {
            if (current && current[part]) {
                current = current[part];
            } else {
                return null;
            }
        }
        return current;
    };

    // Recursive function to render any section/subsection with accounts
    const renderSectionContent = (sectionData, sectionName, parentPath = '', depth = 0, availableYears) => {
        if (!sectionData || typeof sectionData !== 'object') return null;

        const currentPath = parentPath ? `${parentPath}.${sectionName}` : sectionName;
        const isExpanded = expandedSections[currentPath] !== false;

        // Check what this section contains
        const hasAccounts = sectionData.accounts && Array.isArray(sectionData.accounts) && sectionData.accounts.length > 0;
        const hasSubSections = Object.keys(sectionData).some(key =>
            typeof sectionData[key] === 'object' &&
            key !== 'accounts' &&
            key !== 'total_change' &&
            key !== 'accountRanges'
        );
        const hasTotal = sectionData.total_change !== undefined;
        const isClickable = hasAccounts || hasSubSections;

        // Get the display title
        const displayTitle = depth === 0 ? getSectionTitle(sectionName) : sectionName;

        const rows = [];

        // Section header row
        rows.push(
            <tr
                key={`header-${currentPath}`}
                className={`${isClickable ? 'cursor-pointer hover-row' : ''}`}
                onClick={() => isClickable && toggleSection(currentPath)}
                style={{
                    backgroundColor: depth === 0 ? '#f8f9fa' : depth === 1 ? '#ffffff' : 'transparent',
                    fontWeight: 'bold'
                }}
            >
                <td style={{ paddingLeft: `${depth * 1.5}rem` }}>
                    <div className="section-header-rr">
                        {isClickable && (
                            <span style={{ color: '#666', width: '20px' }}>
                                <FontAwesomeIcon
                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                    size="sm"
                                />
                            </span>
                        )}
                        <strong>{displayTitle}</strong>
                    </div>
                </td>
                {availableYears.map(year => {
                    let amount = '';
                    if (hasTotal) {
                        const yearData = processedData?.data[year];
                        if (yearData) {
                            const sectionInYear = getSectionByPath(yearData, currentPath);
                            amount = sectionInYear?.total_change || 0;
                        }
                    }

                    return (
                        <td key={`${currentPath}-${year}`} className="amount-cell" style={{ textAlign: 'right' }}>
                            {hasTotal ? <strong>{formatAmountShowZero(amount)}</strong> : ''}
                        </td>
                    );
                })}
            </tr>
        );

        // If expanded, show content
        if (isExpanded) {
            // First render all subsections
            Object.entries(sectionData)
                .filter(([key, value]) =>
                    typeof value === 'object' &&
                    key !== 'accounts' &&
                    key !== 'total_change' &&
                    key !== 'accountRanges'
                )
                .forEach(([key, value]) => {
                    const subRows = renderSectionContent(value, key, currentPath, depth + 1, availableYears);
                    if (subRows) {
                        rows.push(...subRows);
                    }
                });

            // Then render accounts if any
            if (hasAccounts) {
                sectionData.accounts.forEach(account => {
                    const hasVerifications = showVerifications && account.verifications && account.verifications.length > 0;
                    const isAccountExpanded = expandedAccounts[account.account_id];
                    
                    rows.push(
                        <tr key={`account-${account.account_id}`} className={`hover-row ${hasVerifications ? 'cursor-pointer' : ''}`}>
                            <td style={{ paddingLeft: `${(depth + 1) * 1.5}rem` }}>
                                <div 
                                    className="account-info"
                                    onClick={() => hasVerifications && toggleAccount(account.account_id)}
                                >
                                    {hasVerifications && (
                                        <span style={{ color: '#666', width: '16px', marginRight: '0.5rem' }}>
                                            <FontAwesomeIcon
                                                icon={isAccountExpanded ? faChevronDown : faChevronRight}
                                                size="sm"
                                            />
                                        </span>
                                    )}
                                    <span className="account-number">{account.account_number}</span>
                                    <span className="account-name">{account.account_name}</span>
                                    {hasVerifications && (
                                        <span className="verification-count">
                                            ({account.verifications.length} ver.)
                                        </span>
                                    )}
                                </div>
                            </td>
                            {availableYears.map(year => {
                                let amount = 0;
                                const yearData = processedData?.data[year];
                                if (yearData) {
                                    const sectionDataForYear = getSectionByPath(yearData, currentPath);
                                    if (sectionDataForYear && sectionDataForYear.accounts) {
                                        const accountInYear = sectionDataForYear.accounts.find(
                                            acc => acc.account_id === account.account_id
                                        );
                                        if (accountInYear && accountInYear.change !== undefined && accountInYear.change !== null) {
                                            const changeVal = String(accountInYear.change).replace(/,/g, '');
                                            amount = parseFloat(changeVal);
                                            if (isNaN(amount)) {
                                                amount = 0;
                                            }
                                        } else {
                                            // fallback: find by account_number if IDs differ types
                                            const matchByNum = sectionDataForYear.accounts.find(acc => acc.account_number === account.account_number);
                                            if (matchByNum) {
                                                const changeVal = String(matchByNum.change || 0).replace(/,/g, '');
                                                amount = parseFloat(changeVal);
                                                if (isNaN(amount)) amount = 0;
                                            } else {
                                                amount = 0;
                                            }
                                        }
                                    }
                                }

                                return (
                                    <td key={`${account.account_id}-${year}`} className="amount-cell" style={{ textAlign: 'right' }}>
                                        {formatAmount(amount)}
                                    </td>
                                );
                            })}
                        </tr>
                    );

                    // Add verification details if enabled, available, and account is expanded
                    if (showVerifications && account.verifications && account.verifications.length > 0 && isAccountExpanded) {
                        account.verifications.forEach((verification, vIndex) => {
                            // Get the year from the verification date
                            const verificationYear = new Date(verification.date).getFullYear().toString();
                            
                            rows.push(
                                <tr key={`verification-${account.account_id}-${vIndex}`} className="verification-row">
                                    <td style={{ paddingLeft: `${(depth + 2) * 1.5}rem` }}>
                                        <div className="verification-info">
                                            <small className="verification-details">
                                                <span 
                                                    className="verification-number"
                                                    onClick={() => handleVerificationClick(verification)}
                                                >
                                                    #{verification.verification_number}
                                                </span>
                                                <span className="verification-date">{new Date(verification.date).toLocaleDateString('sv-SE')}</span>
                                                <span className="verification-description">{verification.description}</span>
                                            </small>
                                        </div>
                                    </td>
                                    {availableYears.map(year => {
                                        // Only show amount under the correct year
                                        if (year === verificationYear) {
                                            const amount = verification.transaction_type === 'debit' 
                                                ? verification.amount 
                                                : -verification.amount;
                                            return (
                                                <td key={`verification-${verification.verification_id}-${year}`} className="amount-cell" style={{ textAlign: 'right' }}>
                                                    <small style={{ color: '#666' }}>{formatAmount(amount)}</small>
                                                </td>
                                            );
                                        } else {
                                            // Empty cell for other years
                                            return (
                                                <td key={`verification-${verification.verification_id}-${year}`} className="amount-cell" style={{ textAlign: 'right' }}>
                                                    <small style={{ color: '#ccc' }}>—</small>
                                                </td>
                                            );
                                        }
                                    })}
                                </tr>
                            );
                        });
                    }
                });
            }
        }

        return rows;
    };

    const calculateTotalResult = (yearData) => {
        if (!yearData) return 0;

        const mainSections = [
            'Rörelseintäkter, lagerförändringar m.m.',
            'Rörelsekostnader',
            'Finansiella poster',
            'Bokslutsdispositioner',
            'Skatter'
        ];

        return mainSections.reduce((total, section) => {
            return total + (parseFloat(yearData[section]?.total_change) || 0);
        }, 0);
    };

    const exportToPDF = () => {
        if (!processedData) return;

        const doc = new jsPDF();
        const availableYears = processedData.years || [];

        doc.setFontSize(16);
        doc.text('RESULTATRÄKNING', 20, 20);
        doc.setFontSize(12);
        doc.text(`Jämförelse: ${availableYears.join(', ')}`, 20, 30);

        doc.save(`Resultatrapport_${availableYears.join('_')}.pdf`);
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p className="mt-3">Laddar resultatrapport...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger">
                <Alert.Heading>Fel vid hämtning av data</Alert.Heading>
                <p>{error}</p>
            </Alert>
        );
    }

    if (fiscalYears.length === 0) {
        return (
            <>
                <PageTitle motherMenu="Rapporter" activeMenu="Resultatrapport" />
                <NoData message="Det finns inga räkenskapsår att visa. Skapa ett nytt räkenskapsår för att börja lägga till verifikationer." />
                <div className="text-center mt-4">
                    <Link
                        to="#"
                        onClick={() => history.push(`/${companyId}/settings`)}
                        className="btn btn-primary"
                    >
                        Gå till Inställningar
                    </Link>
                </div>
            </>
        );
    }

    if (!reportData || Object.keys(reportData).length === 0) {
        return (
            <>
                <PageTitle motherMenu="Rapporter" activeMenu="Resultatrapport" />
                <Alert variant="info">Ingen data tillgänglig för det valda räkenskapsåret.</Alert>
            </>
        );
    }

    if (!processedData) {
        return (
            <div className="text-center p-5">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p className="mt-3">Bearbetar data...</p>
            </div>
        );
    }

    const availableYears = processedData.years;
    
    const yearHasData = (yearStruct) => {
        if (!yearStruct) return false;
        const keys = [
            'Rörelseintäkter, lagerförändringar m.m.',
            'Rörelsekostnader',
            'Finansiella poster',
            'Bokslutsdispositioner',
            'Skatter'
        ];
        return keys.some(k => {
            const sec = yearStruct[k];
            if (!sec) return false;
            const tot = parseFloat(sec.total_change || 0);
            return Math.abs(tot) > 0.01;
        });
    };

    const displayYear = availableYears.find(y => yearHasData(processedData.data[y])) || availableYears[0];
    const currentYearData = processedData.data[displayYear];
    const mainSections = [
        'Rörelseintäkter, lagerförändringar m.m.',
        'Rörelsekostnader',
        'Finansiella poster',
        'Bokslutsdispositioner',
        'Skatter'
    ];

    return (
        <>
            <PageTitle motherMenu="Rapporter" activeMenu="Resultatrapport" />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                    <span>År/Period:</span>
                    <Select
                        value={(function() {
                            const fyId = selectedFiscalYear?.fiscal_year_id;
                            const p = selectedPeriod?.value;
                            if (!fyId || !p) return null;
                            for (const group of yearPeriodOptions) {
                                for (const o of group.options) {
                                    const v = o.value;
                                    if (
                                        v.fiscalYear?.fiscal_year_id === fyId &&
                                        v.period?.type === p.type &&
                                        v.period?.startDate === p.startDate &&
                                        v.period?.endDate === p.endDate
                                    ) {
                                        return o;
                                    }
                                }
                            }
                            return null;
                        })()}
                        onChange={handleYearPeriodChange}
                        options={yearPeriodOptions}
                        styles={{
                            container: (base) => ({ ...base, minWidth: '300px' }),
                            menuList: (base) => ({ ...base, maxHeight: 280 })
                        }}
                    />

                    <Form.Check
                        type="switch"
                        id="verifications-switch"
                        label="Visa verifikationer"
                        checked={showVerifications}
                        onChange={() => setShowVerifications(!showVerifications)}
                        style={{ marginLeft: '1rem' }}
                    />
                </div>

                <Button variant="primary" onClick={exportToPDF} disabled={!processedData}>
                    <FontAwesomeIcon icon={faFileDownload} className="me-2" />
                    Exportera PDF
                </Button>
            </div>



            <Card>
                <Card.Header>
                    <h5>Resultaträkning</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <div style={{ overflowX: 'auto' }}>
                        <Table hover className="mb-0 result-table">
                            <colgroup>
                                <col style={{ width: `${Math.max(40, 70 - (availableYears.length - 1) * 10)}%` }} />
                                {availableYears.map(year => (
                                    <col key={year} style={{ width: `${60 / availableYears.length}%` }} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th></th>
                                    {availableYears.map(year => (
                                        <th key={year} className="text-end">{year}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {mainSections.map(sectionName => {
                                    const sectionData = currentYearData?.[sectionName];
                                    if (!sectionData) return null;

                                    const rows = renderSectionContent(sectionData, sectionName, '', 0, availableYears);
                                    return rows;
                                })}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
                <Card.Footer>
                    <div className="result-summary">
                        <div className="row align-items-center">
                            <div className="col">
                                <h6 className="mb-0">ÅRETS RESULTAT</h6>
                            </div>
                            {availableYears.map(year => {
                                const yearData = processedData.data[year];
                                const total = calculateTotalResult(yearData);
                                return (
                                    <div key={year} className="col-auto text-end">
                                        <div className="year-label">{year}</div>
                                        <div className={`result-amount ${total >= 0 ? 'positive' : 'negative'}`}>
                                            {formatAmountWithSign(total)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Card.Footer>
            </Card>

            <style jsx>{`
                .hover-row:hover {
                    background-color: rgba(0, 0, 0, 0.05);
                }
                .cursor-pointer {
                    cursor: pointer;
                }
                .section-header-rr { 
                    display: flex; 
                    align-items: center; 
                    gap: 0.5rem; 
                    padding-left: 1rem;
                }
                .expand-icon { 
                    width: 20px; 
                    color: #666; 
                }
                .account-info { 
                    display: flex; 
                    align-items: center; 
                    gap: 0.5rem; 
                    overflow: hidden;
                }
                .account-number { 
                    color: #666; 
                    min-width: 4rem; 
                    flex-shrink: 0;
                }
                .account-name { 
                    overflow: hidden; 
                    text-overflow: ellipsis; 
                    white-space: nowrap;
                }
                .amount-cell { 
                    text-align: right; 
                }
                .result-summary { 
                    background-color: #f8f9fa; 
                    padding: 1rem; 
                    border-radius: 4px; 
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .year-label { 
                    font-size: 0.8em; 
                    color: #666; 
                    margin-bottom: 0.25rem; 
                }
                .result-amount { 
                    font-weight: bold;
                    font-size: 1.2em;
                }
                .result-amount.positive { 
                    color: #198754; 
                }
                .result-amount.negative { 
                    color: #dc3545; 
                }
                .verification-row {
                    background-color: #fafbfc;
                    font-size: 0.85em;
                }
                .verification-info {
                    margin-left: 1rem;
                }
                .verification-details {
                    display: flex;
                    gap: 1rem;
                    color: #666;
                }
                .verification-number {
                    font-weight: 500;
                    color: #0d6efd;
                    cursor: pointer;
                    text-decoration: underline;
                }
                .verification-number:hover {
                    color: #0a58ca;
                    text-decoration: none;
                }
                .verification-date {
                    color: #6c757d;
                }
                .verification-description {
                    color: #495057;
                }
                .verification-count {
                    font-size: 0.75em;
                    color: #6c757d;
                    margin-left: 0.5rem;
                    font-weight: normal;
                }
                .account-info {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    overflow: hidden;
                }
                .account-info.cursor-pointer {
                    cursor: pointer;
                }
            `}</style>

            {/* Verification Details Modal */}
            <VerificationDetailsModal
                showVerificationDetails={showVerificationModal}
                setShowVerificationDetails={setShowVerificationModal}
                selectedRow={selectedVerification}
                onVerificationUpdated={() => {
                    // Optionally refresh data when verification is updated
                    if (selectedFiscalYear && selectedPeriod) {
                        getResultReport();
                    }
                }}
                onUploadAttachment={() => {
                    // Handle attachment upload if needed
                }}
            />
        </>
    );
};

export default ResultReport;
